/** Ambassador portal routes and sub-nav. Single source for both the router and the tabs. */

export const AMBASSADOR_PORTAL_BASE = '/account/ambassador';

/** Where "Ambassador portal" in the account nav lands, and where a bare /account/ambassador goes. */
export const AMBASSADOR_PORTAL_HOME = `${AMBASSADOR_PORTAL_BASE}/dashboard`;

/**
 * Tab order is the intended reading order of the programme: how am I doing, who have I introduced,
 * what did they book, how do I compare, what have I been paid, what could I win.
 *
 * The design puts five of these across the top and tucks the sixth (Trip of the year) behind a "..."
 * overflow menu, alongside a handful of other pages — Commission split, Playbook, Celebration feed,
 * Refer an ambassador, Directory, Group quote — that don't exist in this build. `overflow: true`
 * marks the one real page that belongs there; the nav only ever renders pages this array lists, so
 * the made-up ones can't leak in even if the design adds more of them later.
 */
export const AMBASSADOR_TABS = [
  { key: 'dashboard', label: 'Dashboard', to: `${AMBASSADOR_PORTAL_BASE}/dashboard` },
  { key: 'leads', label: 'Leads', to: `${AMBASSADOR_PORTAL_BASE}/leads` },
  { key: 'bookings', label: 'Bookings', to: `${AMBASSADOR_PORTAL_BASE}/bookings` },
  { key: 'leaderboard', label: 'Leaderboard', to: `${AMBASSADOR_PORTAL_BASE}/leaderboard` },
  { key: 'invoices', label: 'Invoices', to: `${AMBASSADOR_PORTAL_BASE}/invoices` },
  { key: 'trip-of-the-year', label: 'Trip of the year', to: `${AMBASSADOR_PORTAL_BASE}/trip-of-the-year`, overflow: true }
];

/** True for any route inside the portal, including the bare base. */
export function isAmbassadorPortalRoute(pathname = '') {
  return pathname === AMBASSADOR_PORTAL_BASE || pathname.startsWith(`${AMBASSADOR_PORTAL_BASE}/`);
}

/** Which tab a URL belongs to, or null when the path is not a portal route. */
export function pathToAmbassadorTabKey(pathname = '') {
  if (!isAmbassadorPortalRoute(pathname)) return null;
  const tab = AMBASSADOR_TABS.find(t => pathname === t.to || pathname.startsWith(`${t.to}/`));
  // The bare base redirects to the dashboard, so it reads as the dashboard tab meanwhile.
  return tab ? tab.key : 'dashboard';
}

/** Tabs with the active one marked, for rendering. */
export function getAmbassadorTabs(activeKey = 'dashboard') {
  return AMBASSADOR_TABS.map(tab => ({ ...tab, active: tab.key === activeKey }));
}

/** EUR minor units to a display string, e.g. 123456 -> "€1,234.56". */
export function formatEurCents(cents) {
  return `€${((cents || 0) / 100).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Minor units in an arbitrary currency to a display string. */
export function formatAmount(cents, currency) {
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : `${currency} `;
  return `${symbol}${((cents || 0) / 100).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * The suggested subject, title and message an ambassador starts from.
 *
 * One source for both invite paths. The quick Invite button and the bulk modal send the same
 * template through the same endpoint, and they used to hardcode different copy: the quick path,
 * which is the one most people use, had the weaker impersonal version, and it also promised the
 * link alone would apply the discount.
 *
 * `subject` and `title` are the same string on purpose. The API takes them separately and falls
 * back to the subject when the title is blank, but the ambassador is only ever shown one field to
 * write, so sending two different values would put something on the heading they never typed.
 */
export function ambassadorInviteDefaults(firstName) {
  const name = (firstName || '').trim() || 'A Yacht Week ambassador';
  const headline = `${name} wants you on the yacht`;

  return {
    subject: headline,
    title: headline,
    // Says "use my code", not "the link does it": the link sets a cookie, which a different device
    // or a private window will not carry. The email repeats the code at checkout for the same
    // reason.
    message: "I'm sailing Yacht Week this summer and I'd love you on the yacht. Use my code and "
      + "you'll get a discount when you book, sort it early, the good weeks go first."
  };
}

/** The most addresses one send may carry, matching MAX_RECIPIENTS_PER_SEND on the server. */
export const MAX_RECIPIENTS_PER_SEND = 50;

/** Largest file the CSV reader will open, in bytes. Beyond this it is not a contact list. */
export const MAX_CSV_BYTES = 2 * 1024 * 1024;

const EMAIL_DELIMITERS = /[\s,;<>"']+/;
const EMAIL_TOKEN = /^[^\s,;<>"']+@[^\s,;<>"']+\.[^\s,;<>"']+$/;

/**
 * Every email-shaped string in a blob of text, lowercased and de-duplicated, in order.
 *
 * Splits on the delimiters first and tests each token, rather than scanning one global regex across
 * the whole string. The scanning form is quadratic on input containing no "@", because every start
 * position runs to the end of the string before failing: 2.9 seconds for 60k characters and 37
 * seconds for 200k, which a CSV of prose or a spreadsheet exported with notes would reach easily
 * and freeze the tab doing it. Splitting is linear and gives identical results: 2MB in 6ms.
 */
export function parseEmails(text) {
  const seen = {};

  return String(text || '')
    .split(EMAIL_DELIMITERS)
    .map(token => token.toLowerCase())
    .filter(token => EMAIL_TOKEN.test(token) && !seen[token] && (seen[token] = true));
}

/** Splits one CSV line, honouring quoted fields and doubled quotes inside them. */
function splitCsvLine(line, delimiter) {
  const cells = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      cells.push(cell);
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell);

  return cells;
}

/** Whichever of comma, semicolon or tab splits the header row into the most fields. */
function detectDelimiter(header) {
  return [',', ';', '\t']
    .map(d => ({ d, n: splitCsvLine(header, d).length }))
    .sort((a, b) => b.n - a.n)
    .filter(x => x.n > 1)
    .map(x => x.d)[0] || ',';
}

const EMAIL_HEADER_EXACT = /^(e-?mail|e-?mail[\s_-]*address|mail)$/i;
const EMAIL_HEADER_LOOSE = /e-?mail/i;

/**
 * Addresses from a CSV, read from the email column where the file names one.
 *
 * A whole-file scan is the wrong default for an invite list. A normal CRM export carries addresses
 * in columns nobody meant to invite (an account manager, an unsubscribe log), and pulling those in
 * silently grants the code to people the ambassador never chose and spends their daily quota doing
 * it. So the header is read first and only the matching column is taken.
 *
 * The scan stays as the fallback, for the two cases where there is no column to find: a file with no
 * header at all (often just one address per line), and a header whose email column is named
 * something this cannot recognise. It also catches the case where the chosen column turns out to
 * hold no addresses, e.g. a header called "email_status" listing "valid" and "bounced", where
 * trusting the match would report an empty file.
 *
 * `column` is returned so the UI can say which one was used. A wrong guess is then visible rather
 * than silent, which matters more than getting every header name right.
 *
 * @returns {{emails: string[], column: string|null}} column is null when the fallback scan was used
 */
export function parseCsvEmails(text) {
  const lines = String(text || '').split(/\r\n|\r|\n/).filter(line => line.trim() !== '');
  if (!lines.length) {
    return { emails: [], column: null };
  }

  const delimiter = detectDelimiter(lines[0]);
  const header = splitCsvLine(lines[0], delimiter).map(h => h.trim());
  let index = header.findIndex(h => EMAIL_HEADER_EXACT.test(h));
  if (index === -1) {
    index = header.findIndex(h => EMAIL_HEADER_LOOSE.test(h));
  }

  if (index !== -1) {
    const column = lines.slice(1).map(line => splitCsvLine(line, delimiter)[index] || '');
    const emails = parseEmails(column.join('\n'));
    if (emails.length) {
      return { emails, column: header[index] };
    }
  }

  return { emails: parseEmails(text), column: null };
}
